import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Rating from '@mui/material/Rating';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import moment from 'moment';

import { getListingReviews } from '../../../utils/reviews';

/**
 * What travellers said about a listing.
 *
 * The Reviews tab rendered a hardcoded "No reviews yet." — true when it was
 * written, since reviews did not exist, and it would have stayed on the page
 * indefinitely once they did.
 *
 * Only visible reviews arrive here: `getListingReviews` drops the hidden ones,
 * and the aggregate beside the stars excludes them too, so a moderated review
 * disappears from both the list and the score.
 *
 * A failure to load is not fatal. `getListingReviews` returns [] and logs, so
 * a listing whose reviews cannot be read still shows the listing — the thing
 * the traveller came for.
 */

/** Initials for the avatar. Falls back to a neutral mark, never to a blank. */
function initialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '·';
  return parts
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('');
}

function ReviewRow({ review, showDivider }) {
  const when = review.createdAt ? moment(review.createdAt) : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1.5, py: 2 }}>
        <Avatar sx={{ width: 36, height: 36, fontSize: '.8rem', bgcolor: 'action.selected', color: 'text.secondary' }}>
          {initialsOf(review.authorName)}
        </Avatar>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {/* Reviews carry no name for travellers who signed up before
                  first/last name were collected. */}
              {review.authorName || 'A traveller'}
            </Typography>
            <Rating value={Number(review.rating) || 0} readOnly size="small" />
            {when && when.isValid() && (
              <Typography variant="caption" color="text.secondary">
                {when.format('MMM YYYY')}
              </Typography>
            )}
          </Box>

          {review.comment ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, whiteSpace: 'pre-line' }}
            >
              {review.comment}
            </Typography>
          ) : (
            // A rating with no words is a legitimate review, not a broken one.
            (<Typography variant="body2" color="text.disabled" sx={{ mt: 0.5, fontStyle: 'italic' }}>Rated, no comment left.
                          </Typography>)
          )}
        </Box>
      </Box>

      {showDivider && <Divider />}
    </Box>
  );
}

export function ReviewList({ listingId }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);

    if (!listingId) {
      setReviews([]);
      setLoading(false);
      return undefined;
    }

    getListingReviews(listingId).then((found) => {
      if (!live) return;
      setReviews(found);
      setLoading(false);
    });

    return () => {
      live = false;
    };
  }, [listingId]);

  if (loading) {
    // Skeletons rather than a spinner: the tab has a known shape, and this
    // does not shift the layout when the content lands.
    return (
      <Box sx={{ py: 1 }}>
        {[0, 1].map((n) => (
          <Box key={n} sx={{ display: 'flex', gap: 1.5, py: 2 }}>
            <Skeleton variant="circular" width={36} height={36} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="35%" />
              <Skeleton width="90%" />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (reviews.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No reviews yet.</Typography>
        <Typography variant="caption" color="text.disabled">
          Only travellers who have completed a trip here can leave one.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {reviews.map((review, i) => (
        <ReviewRow key={review.id} review={review} showDivider={i < reviews.length - 1} />
      ))}
    </Box>
  );
}

export default ReviewList;
